import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { History, Search, ChevronDown, ChevronUp, MessageSquare, HelpCircle, CheckCircle, Hash } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import {
  PageHeader, UserAvatar,
  FilterButton, ActiveFilterChips, FilterPanel, FilterField, Input, FilterRow, FilterSelectField,
  useListFilters,
} from "../ui";
import "./AI.scss";

const techStacks = techStacksList;
const difficulties = ["", "easy", "medium", "hard"];
const PAGE_SIZE = 10;

const AdminAIAttemptHistory = ({ embedded = false }) => {
  const [attempts, setAttempts] = useState([]);
  // Declared above useListFilters, whose onChange closes over setPage. Safe either
  // way (a callback body, not a deps array) but it keeps no-use-before-define quiet.
  const [page, setPage] = useState(1);
  // `search` and `studentId` are NOT in the drawer — search stays on the page and
  // the student picker is an AsyncSelect, so they keep their own state and are
  // merged into the query below.
  const [search, setSearch] = useState("");
  const [studentId, setStudentId] = useState("");
  /* ── The FilterPanel pattern (D3) ──
     `empty: ""` on every key: this API reads the empty string as "no filter", not
     the "all" sentinel useListFilters defaults to.
     🔴 minScore/maxScore had NO desktop control at all — they existed only inside
     the `d-md-none` modal, so on the 1280×720 admin viewport they were
     unreachable while activeFilterCount still counted them. One drawer, both
     viewports, all four filters. */
  const listFilters = useListFilters(
    {
      techStack: {
        label: "Tech Stack", empty: "",
        options: techStacks.map((t) => ({ value: t, label: t || "All Tech Stacks" })),
      },
      difficulty: {
        label: "Difficulty", empty: "",
        options: difficulties.map((d) => ({ value: d, label: d || "All Difficulties" })),
      },
      minScore: { label: "Min Score", empty: "" },
      maxScore: { label: "Max Score", empty: "" },
    },
    () => setPage(1),
  );
  /* ⚠️ MEMOISED, and it has to be. This object is a `useEffect` dependency
     below; as a bare literal it would be a new identity on every render, so the
     effect would fire, fetch, setState, re-render, and fire again — an infinite
     request loop. It was `useState` before this migration, which gave it a
     stable identity for free. Moving state into a hook is where that guarantee
     is quietly lost. `listFilters.values` is itself only replaced when a value
     actually changes (useListFilters bails out on an equal set), so this is
     stable in exactly the cases it needs to be. */
  const filterValues = listFilters.values;
  const filters = useMemo(
    () => ({ ...filterValues, search, studentId }),
    [filterValues, search, studentId],
  );
  const [searchInput, setSearchInput] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-attempts/history", {
        params: {
          page,
          limit: PAGE_SIZE,
          ...filters,
        }
      });
      setAttempts(data.attempts || []);
      setTotalRecords(data.total || data.attempts?.length || 0);
    } catch {
      setAttempts([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAttempts();
  }, [filters, page]);

  const loadStudentOptions = async (inputValue) => {
    try {
      const res = await apiClient.get("/students/list", {
        params: { search: inputValue, limit: 20 }
      });
      return (res.data.students || []).map(s => ({
        label: `${s.name} (${s.email})`,
        value: s._id
      }));
    } catch {
      return [];
    }
  };

  // Search commits on blur / Enter, not per keystroke — deliberate, and the
  // reason this page keeps its own input rather than taking ui/SearchInput,
  // which fires on every change.
  const handleSearchBlur = () => {
    if (searchInput !== search) {
      setSearch(searchInput);
      setPage(1);
    }
  };

  const getScoreClass = (score) => {
    if (score >= 70) return "ai-score-badge--high";
    if (score >= 40) return "ai-score-badge--medium";
    return "ai-score-badge--low";
  };

  const getDifficultyClass = (difficulty) => {
    const d = difficulty?.toLowerCase();
    if (d === "easy") return "ai-difficulty-badge--easy";
    if (d === "medium") return "ai-difficulty-badge--medium";
    if (d === "hard") return "ai-difficulty-badge--hard";
    return "";
  };

  // Define table columns
  const columns = [
    {
      key: "index",
      label: "#",
      width: "40px",
      render: (_, index) => (page - 1) * PAGE_SIZE + index + 1
    },
    {
      key: "student",
      label: "Student",
      width: "20%",
      render: (a) => (
        <div className="d-flex align-items-center gap-2">
          <UserAvatar name={a.student?.name} size="sm" />
          <div>
            <div className="fw-medium">{a.student?.name || "-"}</div>
            <div className="text-muted text-sm">{a.student?.email || "-"}</div>
          </div>
        </div>
      )
    },
    {
      key: "question",
      label: "Question",
      width: "25%",
      render: (a) => {
        const q = a.question?.question || "-";
        return (
          <div className="text-sm" title={q}>
            {q.length > 80 ? q.substring(0, 80) + "..." : q}
          </div>
        );
      }
    },
    {
      key: "techStack",
      label: "Tech Stack",
      width: "10%",
      render: (a) => a.question?.techStack || "-"
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (a) => (
        <span className={`ai-difficulty-badge ${getDifficultyClass(a.question?.difficulty)}`}>
          {a.question?.difficulty || "-"}
        </span>
      )
    },
    {
      key: "score",
      label: "Score",
      width: "8%",
      render: (a) => (
        <span className={`ai-score-badge ${getScoreClass(a.score)}`}>
          {a.score ?? "-"}
        </span>
      )
    },
    {
      key: "submittedOn",
      label: "Submitted On",
      width: "12%",
      render: (a) => a.createdAt 
        ? new Date(a.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) 
        : "-"
    },
    {
      key: "expand",
      label: "",
      width: "40px",
      render: (a) => {
        const rowId = a._id || a.id;
        const isExpanded = expandedRows.has(rowId);
        return (
          <button
            className="ai-expand-btn"
            onClick={(e) => { e.stopPropagation(); toggleRow(rowId); }}
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
            title={isExpanded ? "Hide details" : "View details"}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        );
      }
    }
  ];

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "var(--input-bg)",
      borderColor: "var(--border-primary)",
      borderRadius: "8px",
      minHeight: "38px",
      "&:hover": {
        borderColor: "var(--text-tertiary)"
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--card-bg)",
      border: "1px solid var(--border-primary)",
      borderRadius: "8px",
      boxShadow: "0 4px 12px var(--card-shadow)"
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "var(--bg-hover)" : "transparent",
      color: state.isSelected ? "#fff" : "var(--text-primary)",
      "&:hover": {
        backgroundColor: state.isSelected ? "#3b82f6" : "var(--bg-hover)"
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text-primary)"
    }),
    input: (base) => ({
      ...base,
      color: "var(--text-primary)"
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text-tertiary)"
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "var(--border-primary)"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--text-tertiary)",
      "&:hover": {
        color: "var(--text-primary)"
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "var(--text-tertiary)",
      "&:hover": {
        color: "var(--text-primary)"
      }
    })
  };

  return (
    <div className="ai-page">
      {!embedded && (
        <PageHeader
          icon={History}
          title="AI Attempts"
          description="View all student AI interview attempts and scores"
        />
      )}

      {/* Compact Toolbar */}
      <div className="ai-toolbar">
        {/* Student search using AsyncSelect */}
        <div style={{ minWidth: "280px", flex: "0 0 auto" }}>
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadStudentOptions}
            value={selectedStudent}
            onChange={(selected) => {
              setSelectedStudent(selected);
              setStudentId(selected?.value || "");
              setPage(1);
            }}
            placeholder="Search student..."
            isClearable
            styles={customSelectStyles}
          />
        </div>

        <div className="ai-search">
          <Search size={16} className="ai-search-icon" />
          <input
            type="text"
            className="ai-search-input"
            placeholder="Search answer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        {/* ONE filter UI, not two. This was a `d-none d-md-block` inline strip
            beside a `d-md-none` button-and-modal — two implementations of the same
            filters, either of which could be updated without the other, and they
            had already diverged: the strip carried two of the four. Same defect
            InstructorsPage had (Phase 6.3). */}
        <div className="ai-toolbar-actions">
          <FilterButton count={listFilters.count} onClick={listFilters.open} />
        </div>
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      {/* Data Table */}
      <DataTable
        data={attempts}
        columns={columns}
        loading={loading}
        hoverable={true}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: totalRecords,
          onPageChange: setPage
        }}
        emptyMessage="No AI attempts found"
        renderExpandedRow={(row) => {
          const rowId = row._id || row.id;
          if (!expandedRows.has(rowId)) return null;
          return (
            <tr className="ai-expanded-row" key={`expanded-${rowId}`}>
              <td colSpan={columns.length} style={{ padding: 0, border: "none" }}>
                <div className="ai-expanded-detail">
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <HelpCircle size={14} />
                      Question
                    </div>
                    <div className="ai-detail-value">{row.question?.question || "-"}</div>
                  </div>
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <MessageSquare size={14} />
                      Student's Answer
                    </div>
                    <div className="ai-detail-value">{row.answer || "-"}</div>
                  </div>
                  <div className="ai-detail-section">
                    <div className="ai-detail-label">
                      <CheckCircle size={14} />
                      AI Feedback
                    </div>
                    <div className="ai-detail-value">{row.feedback || "-"}</div>
                  </div>
                  {row.attemptCount > 1 && (
                    <div className="ai-detail-section ai-detail-section--inline">
                      <div className="ai-detail-label">
                        <Hash size={14} />
                        Attempts
                      </div>
                      <div className="ai-detail-value">{row.attemptCount}</div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        }}
      />

      <FilterPanel {...listFilters.panelProps} title="Filter Attempts">
        <FilterSelectField {...listFilters.selectProps("techStack")} />
        <FilterSelectField {...listFilters.selectProps("difficulty")} />
        <FilterRow>
          <FilterField label="Min Score">
            <Input size="lg"
              type="number"
              value={listFilters.values.minScore}
              onChange={(e) => listFilters.set("minScore", e.target.value)}
              placeholder="Min Score"
            />
          </FilterField>
          <FilterField label="Max Score">
            <Input size="lg"
              type="number"
              value={listFilters.values.maxScore}
              onChange={(e) => listFilters.set("maxScore", e.target.value)}
              placeholder="Max Score"
            />
          </FilterField>
        </FilterRow>
      </FilterPanel>
    </div>
  );
};

export default AdminAIAttemptHistory;
